// Channels tab — every channel across every strategy owned by this API key.
// Provider-card grid grouping (TestUi sink / Discord / Custom webhook) plus the
// list. Hover-pick to delete/copy via the row's dropdown menu.

import { useEffect, useMemo, useState } from "react";
import { Plus, AlertCircle, Webhook } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useStrategies } from "../../hooks/useStrategies";
import { useNavigation } from "../../state/NavigationContext";
import type { AlertChannelDto } from "../../api/types";
import { listChannels } from "../../api/channels";
import { ChannelRow } from "./ChannelRow";

export function ChannelsPage() {
  const { strategies, loading: strategiesLoading, error: strategiesError, reload: reloadStrategies } = useStrategies();
  const { navigateTo } = useNavigation();
  const [channels, setChannels] = useState<{ channel: AlertChannelDto; strategyId: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (strategies.length === 0) {
      setChannels([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const lists = await Promise.all(
          strategies.map((s) => listChannels(s.id).then((cs) => ({ id: s.id, cs }))),
        );
        if (cancelled) return;
        const flat = lists.flatMap((l) => l.cs.map((c) => ({ channel: c, strategyId: l.id })));
        setChannels(flat);
        setError(null);
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [strategies]);

  const strategiesById = useMemo(() => new Map(strategies.map((s) => [s.id, s])), [strategies]);
  const enabledCount = channels.filter((c) => c.channel.enabled).length;

  if (strategiesLoading || loading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-[120px]" />
        <Skeleton className="h-[100px]" />
      </div>
    );
  }
  if (strategiesError || error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="size-4" />
        <AlertTitle>Couldn't load channels</AlertTitle>
        <AlertDescription>{strategiesError ?? error}</AlertDescription>
      </Alert>
    );
  }
  if (channels.length === 0) {
    return (
      <Card className="p-10 text-center bg-card-elevated max-w-2xl mx-auto mt-8">
        <div className="mx-auto mb-3 grid place-items-center size-12 rounded-full bg-primary/10 text-primary">
          <Webhook className="size-5" />
        </div>
        <h2 className="text-lg font-semibold tracking-tight mb-2">No channels yet</h2>
        <p className="text-sm text-muted-foreground max-w-md mx-auto mb-4 leading-relaxed">
          Channels are attached to strategies — they're not free-standing. Open the
          Builder, scroll to "Webhook channels", and pick a delivery target. The default
          "TestUi sink" routes alerts to this UI's own /webhooks/sink so you can verify
          the payload shape without setting up a real bot endpoint first.
        </p>
        <Button
          onClick={() => navigateTo("builder")}
          className="bg-primary hover:bg-primary/90 text-primary-foreground"
        >
          <Plus className="size-4" />
          Go to Builder
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card className="p-3 bg-card-elevated flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs">
          <Webhook className="size-3.5 text-primary" />
          <span>
            <strong className="text-foreground">{channels.length}</strong>{" "}
            channel{channels.length === 1 ? "" : "s"} across{" "}
            <strong className="text-foreground">{strategies.length}</strong>{" "}
            strateg{strategies.length === 1 ? "y" : "ies"}
          </span>
          <span className="text-muted-foreground">·</span>
          <span className="text-emerald-400">{enabledCount} enabled</span>
        </div>
        <Button onClick={() => navigateTo("builder")} variant="outline" size="sm">
          <Plus className="size-3.5" />
          Add via Builder
        </Button>
      </Card>

      <div className="grid gap-3">
        {channels.map(({ channel, strategyId }) => (
          <ChannelRow
            key={channel.id}
            channel={channel}
            ownerStrategy={strategiesById.get(strategyId)}
            onDeleted={reloadStrategies}
          />
        ))}
      </div>
    </div>
  );
}
